import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash } from 'react-icons/fa';

const PagesManagement = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPage, setCurrentPage] = useState({
    id: null,
    slug: '',
    title: '',
    subtitle: '',
    content: '',
    meta_description: '',
    is_active: true,
    order_index: 0
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/pages/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setPages(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      alert('Failed to fetch pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editMode) {
        await axios.put(`${API_URL}/pages/${currentPage.id}`, currentPage, { headers });
        alert('Page updated successfully!');
      } else {
        await axios.post(`${API_URL}/pages`, currentPage, { headers });
        alert('Page created successfully!');
      }

      resetForm();
      fetchPages();
    } catch (error) {
      console.error('Error saving page:', error);
      alert('Failed to save page');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (page) => {
    setCurrentPage(page);
    setEditMode(true);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/pages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Page deleted successfully!');
      fetchPages();
    } catch (error) {
      console.error('Error deleting page:', error);
      alert('Failed to delete page');
    }
  };

  const resetForm = () => {
    setCurrentPage({
      id: null,
      slug: '',
      title: '',
      subtitle: '',
      content: '',
      meta_description: '',
      is_active: true,
      order_index: 0
    });
    setEditMode(false);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentPage({
      ...currentPage,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  if (loading && !showForm) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-700"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Pages Management</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-maroon-700 hover:bg-maroon-800 text-white px-6 py-3 rounded-lg flex items-center transition-all duration-300"
        >
          <FaPlus className="mr-2" />
          {showForm ? 'Cancel' : 'Add New Page'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {editMode ? 'Edit Page' : 'Create New Page'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Slug *</label>
                <input
                  type="text"
                  name="slug"
                  value={currentPage.slug}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="about-us"
                />
                <p className="text-xs text-gray-500 mt-1">URL-friendly name (e.g., about-us)</p>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Order Index</label>
                <input
                  type="number"
                  name="order_index"
                  value={currentPage.order_index}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Title *</label>
              <input
                type="text"
                name="title"
                value={currentPage.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="Page Title"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Subtitle</label>
              <input
                type="text"
                name="subtitle"
                value={currentPage.subtitle}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="Page Subtitle"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Content</label>
              <textarea
                name="content"
                value={currentPage.content}
                onChange={handleChange}
                rows="8"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="Page content..."
              ></textarea>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Meta Description</label>
              <textarea
                name="meta_description"
                value={currentPage.meta_description}
                onChange={handleChange}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="SEO meta description..."
              ></textarea>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={currentPage.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-maroon-700 focus:ring-maroon-700 border-gray-300 rounded"
              />
              <label className="ml-2 text-gray-700">Active</label>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-maroon-700 hover:bg-maroon-800 text-white px-6 py-3 rounded-lg transition-all duration-300 disabled:bg-gray-400"
              >
                {loading ? 'Saving...' : (editMode ? 'Update Page' : 'Create Page')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pages List */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Slug
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pages.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  No pages found. Create your first page!
                </td>
              </tr>
            ) : (
              pages.map((page) => (
                <tr key={page.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{page.title}</div>
                    {page.subtitle && (
                      <div className="text-sm text-gray-500">{page.subtitle}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {page.slug}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      page.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {page.is_active ? (
                        <><FaEye className="mr-1 mt-0.5" /> Active</>
                      ) : (
                        <><FaEyeSlash className="mr-1 mt-0.5" /> Inactive</>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {page.order_index}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(page)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      <FaEdit className="inline mr-1" /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(page.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FaTrash className="inline mr-1" /> Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PagesManagement;
